import { useEffect } from 'react';

import { useModal } from 'src/core/common/hooks/useModalGrapes';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';

import { useTriggerSurvey } from '../../event-tracking/useTriggerSurvey';
import { useCostCenterPolling } from '../../hooks/useCostCenterPolling';
import {
  type CostCenterReplacePreview,
  fetchCompaniesToReassign,
} from '../../hooks/useCostCenterReplacePreviewByCompany';
import { useMigrateToCrossEntityCostCenter } from '../../hooks/useMigrateToCrossEntityCostCenter';
import { useUpdateOrganisationSetting } from '../../hooks/useUpdateOrganisationSetting';
import { type Setting } from '../../setting';
import { CommonModal } from '../CommonModal';
import {
  CostCenterAssignmentContent,
  type CostCenterAssignmentFormValues,
} from '../CostCenterAssignmentContent';
import { CostCenterReassignmentModal } from '../CostCenterReassignmentModal';

export const CostCenterAssignmentEditModal = ({
  setting,
  isOpen,
  mode,
  onCancel,
}: {
  setting: Setting;
  isOpen: boolean;
  mode: 'singleModal' | 'stepModal';
  onCancel: () => void;
}) => {
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();
  const triggerSurvey = useTriggerSurvey();
  const { mutate: updateOrganisationSetting } =
    useUpdateOrganisationSetting(setting);
  const { mutate: migrateToCrossEntityCostCenter } =
    useMigrateToCrossEntityCostCenter();
  const { isSubmitting, startPolling, stopPolling } = useCostCenterPolling({
    type: 'update',
    settings: setting,
    onSuccess: onCancel,
  });

  const [companiesToReassignModal, showCompaniesToReassignModal] = useModal<{
    companiesToReassign: CostCenterReplacePreview[];
    values: CostCenterAssignmentFormValues;
  }>(({ isOpen: isOpenModal, onClose, companiesToReassign, values }) => {
    if (!isOpenModal) {
      return <></>;
    }
    return (
      <CostCenterReassignmentModal
        iconName="triangle-warning"
        submitButtonText={t(
          'multiEntityHub.costCenters.reassign.confirmReassign',
        )}
        submitButtonVariant="primaryBrand"
        settings={setting}
        iconVariant="warning"
        title={t('multiEntityHub.costCenters.reassign.titleReassign', {
          costCenterName: setting.name,
        })}
        subtitle={t('multiEntityHub.costCenters.reassign.descriptionReassign', {
          costCenterName: setting.name,
        })}
        companiesCostCentersToReplace={companiesToReassign}
        onCancel={onClose}
        onConfirm={async () => {
          try {
            await handleAssignmentComplete(values);
            onClose();
          } catch (error) {
            pushNotif({
              type: NotificationType.Danger,
              message: t(
                'multiEntityHub.costCenters.messages.errorUpdateCostCenter',
              ),
            });
            throw error;
          }
        }}
      />
    );
  });

  const handleAssignmentComplete = async (
    values: CostCenterAssignmentFormValues,
  ) => {
    if (
      setting.type === 'entity-specific' &&
      mode === 'singleModal' &&
      setting.entities?.length === 1
    ) {
      // in case if we want to migrate from entity-specific to cross-entity
      try {
        startPolling();
        await migrateToCrossEntityCostCenter({
          type: 'costCenter',
          existingEntity: {
            // since single entity cost center has only one entity, we use the first one
            id: setting.entities[0].costCenterId,
            companyId: setting.entities[0].companyId,
          },
          autoAssignFutureEntities: values.autoAssignFutureEntities,
          companyIds: values.companyIds,
        });
        triggerSurvey();
      } catch (error) {
        stopPolling();
        pushNotif({
          type: NotificationType.Danger,
          message: t(
            'multiEntityHub.costCenters.messages.errorUpdateCostCenter',
          ),
        });
        throw error;
      }
    } else {
      try {
        startPolling();
        await updateOrganisationSetting({
          companyIds: values.companyIds,
          autoAssignFutureEntities: values.autoAssignFutureEntities,
        });
        triggerSurvey();
      } catch (error) {
        stopPolling();
        pushNotif({
          type: NotificationType.Danger,
          message: t(
            'multiEntityHub.costCenters.messages.errorUpdateCostCenter',
          ),
        });
        throw error;
      }
    }
  };

  const handleAssignmentSubmit = async (
    values: CostCenterAssignmentFormValues,
  ) => {
    const unassignedCompanies = setting.entities?.filter(
      (entity) => !values.companyIds.includes(entity.companyId),
    );
    const companiesToReassign = await fetchCompaniesToReassign(
      unassignedCompanies ?? [],
    );
    // if there are some companies with their cost centers that should be replaced, we should open a modal before the assignment is completed
    if (companiesToReassign.length > 0) {
      showCompaniesToReassignModal({ companiesToReassign, values });
    } else {
      handleAssignmentComplete(values);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      stopPolling();
    }
  }, [isOpen, stopPolling]);

  return (
    <>
      {companiesToReassignModal}
      <CommonModal
        isOpen={isOpen}
        title={t('multiEntityHub.costCenters.modal.assignment.title')}
        subtitle={t('multiEntityHub.costCenters.modal.assignment.subtitle')}
        iconName="building-bank"
        onClose={onCancel}
      >
        <CostCenterAssignmentContent
          onComplete={handleAssignmentSubmit}
          onCancel={onCancel}
          mode="singleModal"
          costCenterName={setting?.name}
          setting={setting}
          isSubmitting={isSubmitting}
        />
      </CommonModal>
    </>
  );
};
